import { fromCents, isBrokerChannel } from 'utils/helpers';
import queryStringParser from '../queryStringParser';
import { APPLICATION_FLOW } from '../../consts/loan/applicationFlow';
import { PRODUCT_TYPES } from '../../consts/products';

const APPROVAL_STATE = {
  AUTO_APPROVAL: 'AUTO_APPROVAL',
  MANUAL: 'MANUAL',
};

/**
 * Function to collect proper meta data for Google Analytics
 * @param {Object} storeState
 * @param {int|String} step - current step
 * @param {Object} data - customer entered data or other data passed to this function
 * @returns {Object} used for sending it to GA from middleware
 */
const getAnalyticsObjectAU = (step, storeState = null, data = {}) => {
  if (storeState) {
    const { authentication, loan } = storeState;
    const {
      id: productId,
      productType,
      selectedProduct,
      availablePrincipalsIndex,
      rejected,
      channel,
    } = loan;
    const { id: userId, isOldCustomer } = authentication;
    const { uuid, saccDocumentStatus, provisoStatus } = data;
    const maxPrincipalIndex = availablePrincipalsIndex && availablePrincipalsIndex.length - 1;
    const offerPresented = maxPrincipalIndex
      ? fromCents(availablePrincipalsIndex[maxPrincipalIndex])
      : null;
    const offerSelected = selectedProduct.firstDrawAmount || selectedProduct.principal;
    const isLegalUpgrade = step === 'checkoutSelfservice' && data.page === 'legal';
    const isThankyouUpgrade = step === 'checkoutSelfservice' && data.page === 'thank_you';
    const isThankYouPage = step === 'thank_you';

    const setEventName = () => {
      if (rejected) {
        return 'rejected';
      }

      if (step === 'sign_contract') {
        return 'sign_contract';
      }

      if (isLegalUpgrade || isThankyouUpgrade) {
        return 'checkoutSelfservice';
      }

      if (step === 'loginSelfservice') {
        return 'loginSelfservice';
      }

      return 'application';
    };

    const setProductName = () => {
      if (isLegalUpgrade || isThankyouUpgrade) {
        if (productType === 'CREDIT_LINE') {
          return 'CL_UPGRADE';
        }

        if (productType === 'INSTALLMENT') {
          return 'IL_UPGRADE';
        }
      }

      return productType;
    };

    const productTarget = setProductName();

    const setApplicationFlow = () => {
      const isBroker = isBrokerChannel(channel);
      const { ocm } = queryStringParser.parse(location.search);

      if (INCREASE_APP) {
        return APPLICATION_FLOW.UPGRADE;
      }

      if (isBroker) {
        return APPLICATION_FLOW.BROKER;
      }

      if (ocm) {
        return APPLICATION_FLOW.OCM;
      }

      return APPLICATION_FLOW.DEFAULT;
    };

    const exampleObjectToDataLayer = {
      event: setEventName(),
      page: step, // if I missed something please add
      target: productTarget, // IL/CL/UPGRADE/DOWNGRADE/EVENGRADE etc
      clientType: isOldCustomer ? 'returning' : 'new', // user type
      applicationFlow: setApplicationFlow(), // DEFAULT/BROKER/OCM/UPGRADE
    };

    if (userId) {
      exampleObjectToDataLayer.userId = userId;
    }

    if (productId) {
      exampleObjectToDataLayer.applicationID = productId;
    }

    if (offerPresented) {
      exampleObjectToDataLayer.offerPresented = offerPresented;
    }

    if (offerSelected && !isThankyouUpgrade) {
      exampleObjectToDataLayer.offerSelected = fromCents(offerSelected);

      if (productTarget === PRODUCT_TYPES.INSTALLMENT) {
        exampleObjectToDataLayer.product = offerSelected <= 200000 ? PRODUCT_TYPES.SACC : PRODUCT_TYPES.MACC;
      } else {
        exampleObjectToDataLayer.product = PRODUCT_TYPES.CREDIT_LINE;
      }
    }
    if (uuid) {
      exampleObjectToDataLayer.uuid = uuid;
    }

    if (saccDocumentStatus) {
      exampleObjectToDataLayer.saccDocumentStatus = saccDocumentStatus;
    }

    if (isLegalUpgrade) {
      exampleObjectToDataLayer.pageView = 'contractChanges_legal';
    }

    if (isThankyouUpgrade) {
      exampleObjectToDataLayer.pageView = 'contractChanges_thankyou';
      exampleObjectToDataLayer.amount = fromCents(offerSelected);
    }

    if (provisoStatus) {
      const isStatusObject = typeof provisoStatus === 'object';
      const statusValue = isStatusObject ? provisoStatus.status : provisoStatus;
      const statusMessage = isStatusObject ? provisoStatus.status_message : null;
      const statusCode = isStatusObject ? provisoStatus.status_code : null;
      const institutionValue = isStatusObject ? (provisoStatus.institution || provisoStatus.data) : null;
      const institution = institutionValue && typeof institutionValue === 'object'
        ? JSON.stringify(institutionValue)
        : institutionValue;
      const source = isStatusObject ? provisoStatus.source : null;

      if (statusValue) {
        exampleObjectToDataLayer.proviso_status = `${statusValue}`;
      }

      if (statusMessage) {
        exampleObjectToDataLayer.proviso_status_message = `${statusMessage}`;
      }

      if (statusCode) {
        exampleObjectToDataLayer.proviso_status_code = `${statusCode}`;
      }

      if (institution) {
        exampleObjectToDataLayer.proviso_institution = `${institution}`;
      }

      if (source) {
        exampleObjectToDataLayer.proviso_source = `${source}`;
      }
    }

    if (isThankYouPage) {
      const isAutoApproved = loan.status === 'APPROVED';
      exampleObjectToDataLayer.autoApprove = isAutoApproved ? APPROVAL_STATE.AUTO_APPROVAL : APPROVAL_STATE.MANUAL;
    }

    return exampleObjectToDataLayer;
  }

  if (step) {
    return {
      page: step,
    };
  }

  return {};
};

export default getAnalyticsObjectAU;
